import React, { useState, useEffect } from 'react';
import { 
  Truck, Package, Zap, Shield, Star, TrendingUp, Clock, MapPin, 
  Radio, DollarSign, Container, Box, Snowflake, CheckCircle, Award, 
  Search, FileCheck, AlertTriangle, Wrench, UserCheck, Phone, Mail, 
  MapPinned, ChevronDown, Menu, X, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  services, stats, features, testimonials, fleet, safety, 
  safetyFeatures, faqs, contactInfo
} from '../data/mock';

const iconMap = {
  Truck, Package, Zap, Shield, Star, TrendingUp, Clock, MapPin, 
  Radio, DollarSign, Container, Box, Snowflake, CheckCircle, Award, 
  Search, FileCheck, AlertTriangle, Wrench, UserCheck
};

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <h1 className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>WINDFALL LOGISTICS INC</h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className={`font-medium transition-colors hover:text-green-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}>Services</button>
              <button onClick={() => scrollToSection('about')} className={`font-medium transition-colors hover:text-green-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}>About</button>
              <button onClick={() => scrollToSection('fleet')} className={`font-medium transition-colors hover:text-green-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}>Fleet</button>
              <button onClick={() => scrollToSection('contact')} className={`font-medium transition-colors hover:text-green-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}>Contact</button>
              <a href={`tel:${contactInfo.phoneRaw}`}>
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Us
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? (
                <X className={isScrolled ? 'text-gray-900' : 'text-white'} />
              ) : (
                <Menu className={isScrolled ? 'text-gray-900' : 'text-white'} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-gray-700 hover:text-green-600">Services</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-gray-700 hover:text-green-600">About</button>
              <button onClick={() => scrollToSection('fleet')} className="block w-full text-left py-2 text-gray-700 hover:text-green-600">Fleet</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-gray-700 hover:text-green-600">Contact</button>
              <a href={`tel:${contactInfo.phoneRaw}`} className="block">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1778629794381-d3ae4a54311c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx3aGl0ZSUyMGRlbGl2ZXJ5JTIwdmFuJTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc5MTk3ODAyNnww&ixlib=rb-4.1.0&q=85)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/50 to-purple-900/30" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            FREIGHT THAT MOVES
            <br />
            <span className="text-green-400">AMERICA</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto">
            Speed. Precision. Trust. Every shipment delivered with unmatched excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => scrollToSection('contact')} 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
            >
              GET STARTED
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => scrollToSection('services')} 
              size="lg" 
              variant="outline" 
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 px-8 py-6 text-lg"
            >
              LEARN MORE
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={stat.id} className="text-center animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="text-4xl md:text-5xl font-bold text-green-600 mb-2">{stat.number}</div>
                  <div className="text-sm font-semibold text-gray-900">{stat.label}</div>
                  <div className="text-xs text-gray-600">{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">OUR SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              COMPLETE LOGISTICS SOLUTIONS
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Windfall Logistics Inc delivers comprehensive freight solutions across all 50 states. 
              Our modern fleet and experienced team ensure your cargo reaches its destination safely, on time, and within budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-green-500"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-600 transition-colors">
                      <Icon className="w-8 h-8 text-green-600 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-xl font-bold">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">{service.details}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About/Features Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">WHY WINDFALL LOGISTICS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">EXCELLENCE IN EVERY MILE</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div 
                  key={feature.id} 
                  className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="company-info" className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">WINDFALL LOGISTICS INC</h2>
            <p className="text-lg text-gray-300">Your Trusted Logistics Partner</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm">
              <MapPinned className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">ADDRESS</h3>
              <p className="text-gray-300">{contactInfo.address}</p>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm">
              <Phone className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">PHONE</h3>
              <a href={`tel:${contactInfo.phoneRaw}`} className="text-gray-300 hover:text-green-400 transition-colors">
                {contactInfo.phone}
              </a>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl backdrop-blur-sm">
              <Mail className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">EMAIL</h3>
              <a href={`mailto:${contactInfo.email}`} className="text-gray-300 hover:text-green-400 transition-colors">
                {contactInfo.email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">TESTIMONIALS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              WHAT OUR <span className="text-green-600">CUSTOMERS</span> SAY
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id} 
                className="hover:shadow-xl transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="pt-6">
                  <div className="text-5xl text-green-600 mb-4">"</div>
                  <p className="text-gray-700 mb-6 italic">{testimonial.quote}</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                    <p className="text-xs text-green-600 mt-1">{testimonial.shipments}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section id="fleet" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">OUR FLEET</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              MODERN <span className="text-green-600">EQUIPMENT</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {fleet.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card 
                  key={item.id} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader className="text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-10 h-10 text-green-600" />
                    </div>
                    <CardTitle className="text-xl font-bold">{item.type}</CardTitle>
                    <div className="text-3xl font-bold text-green-600">{item.count}</div>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {item.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">SAFETY FIRST</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              CERTIFIED & <span className="text-green-600">COMPLIANT</span>
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-3xl mx-auto">
              Our commitment to safety and compliance ensures your cargo is in the most capable hands.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {safety.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div 
                  key={item.id} 
                  className="text-center p-6 bg-gray-50 rounded-xl"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900 mb-2">{item.value}</div>
                  <div className="text-sm text-gray-600">{item.title}</div>
                </div>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {safetyFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div 
                  key={feature.id} 
                  className="p-6 bg-gray-50 rounded-xl hover:bg-green-50 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Icon className="w-10 h-10 text-green-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-green-600 font-semibold mb-2">FREQUENTLY ASKED</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">QUESTIONS</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white rounded-lg px-6 border-none shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">READY TO SHIP?</h2>
          <p className="text-xl text-green-100 mb-12">
            Get an instant quote or speak with our logistics experts. We're here 24/7 to handle your freight needs.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <a href={`tel:${contactInfo.phoneRaw}`} className="block">
              <Card className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer bg-white">
                <CardContent className="pt-6 text-center">
                  <Phone className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Call Now</h3>
                  <p className="text-gray-600">{contactInfo.phone}</p>
                </CardContent>
              </Card>
            </a>
            <a href={`mailto:${contactInfo.email}`} className="block">
              <Card className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer bg-white">
                <CardContent className="pt-6 text-center">
                  <Mail className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Email Us</h3>
                  <p className="text-gray-600 text-sm">{contactInfo.email}</p>
                </CardContent>
              </Card>
            </a>
            <button onClick={() => scrollToSection('company-info')}>
              <Card className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer bg-white">
                <CardContent className="pt-6 text-center">
                  <MapPinned className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Visit Us</h3>
                  <p className="text-gray-600">View our location</p>
                </CardContent>
              </Card>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">WINDFALL LOGISTICS INC</h2>
            <p className="text-gray-400 mb-4">Delivering Excellence Across America</p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-400">
              <span>{contactInfo.address}</span>
              <span className="hidden sm:inline">•</span>
              <a href={`tel:${contactInfo.phoneRaw}`} className="hover:text-green-400 transition-colors">{contactInfo.phone}</a>
              <span className="hidden sm:inline">•</span>
              <a href={`mailto:${contactInfo.email}`} className="hover:text-green-400 transition-colors">{contactInfo.email}</a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800 text-gray-500 text-sm">
              © {new Date().getFullYear()} Windfall Logistics Inc. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
